import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  apiMedicalRecordsForParentGet$Json,
  apiMedicalRecordsForParentPut$Json,
  AmoxcalliMedicalRecordDto,
} from 'amoxcalli-academic-api';

import { environment } from '../../../../environments/environment';
import { HistorialMedicoActions } from './historial-medico.actions';
import { HistorialMedico } from './historial-medico.state';

function toHistorialMedico(dto: AmoxcalliMedicalRecordDto): HistorialMedico {
  return {
    exists: dto.exists ?? false,
    tipoSangre: dto.tipoSangre ?? null,
    contactoPrincipalNombre: dto.contactoPrincipalNombre ?? '',
    contactoPrincipalParentesco: dto.contactoPrincipalParentesco ?? '',
    contactoPrincipalTelCelular: dto.contactoPrincipalTelCelular ?? '',
    contactoPrincipalTelTrabajo: dto.contactoPrincipalTelTrabajo ?? null,
    contactoSecundarioNombre: dto.contactoSecundarioNombre ?? null,
    contactoSecundarioParentesco: dto.contactoSecundarioParentesco ?? null,
    contactoSecundarioTelCelular: dto.contactoSecundarioTelCelular ?? null,
    contactoSecundarioTelTrabajo: dto.contactoSecundarioTelTrabajo ?? null,
    alergiaMedicamentos: dto.alergiaMedicamentos ?? null,
    alergiaAlimentos: dto.alergiaAlimentos ?? null,
    alergiaAmbiental: dto.alergiaAmbiental ?? null,
    sinAlergiasConocidas: dto.sinAlergiasConocidas ?? false,
    usaLentes: dto.usaLentes ?? false,
    usaAparatoAuditivo: dto.usaAparatoAuditivo ?? false,
    usaOrtopedico: dto.usaOrtopedico ?? false,
    otroApoyo: dto.otroApoyo ?? null,
    asma: dto.asma ?? false,
    convulsiones: dto.convulsiones ?? false,
    diabetesInfantil: dto.diabetesInfantil ?? false,
    problemasCardiacos: dto.problemasCardiacos ?? false,
    otroPadecimiento: dto.otroPadecimiento ?? null,
    tomaMedicamentoRegular: dto.tomaMedicamentoRegular ?? false,
    nombreMedicamento: dto.nombreMedicamento ?? null,
    dosisHorario: dto.dosisHorario ?? null,
    requiereAdministracionEscolar: dto.requiereAdministracionEscolar ?? false,
    tieneRestriccionEF: dto.tieneRestriccionEF ?? false,
    motivoRestriccionEF: dto.motivoRestriccionEF ?? null,
    restriccionesAlimentarias: dto.restriccionesAlimentarias ?? null,
    notasAdicionales: dto.notasAdicionales ?? null,
  };
}

@Injectable()
export class HistorialMedicoEffects {
  private readonly actions$ = inject(Actions);
  private readonly http     = inject(HttpClient);

  loadRecord$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HistorialMedicoActions.loadRecord),
      switchMap(({ studentId }) =>
        apiMedicalRecordsForParentGet$Json(this.http, environment.serverUrl.academic, { studentId }).pipe(
          map((response) =>
            HistorialMedicoActions.loadRecordSuccess({ record: toHistorialMedico(response.body ?? {}) })
          ),
          catchError(() =>
            of(HistorialMedicoActions.loadRecordFailure({ error: 'No se pudo cargar el historial médico.' }))
          )
        )
      )
    )
  );

  saveRecord$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HistorialMedicoActions.saveRecord),
      switchMap(({ studentId, record }) =>
        apiMedicalRecordsForParentPut$Json(this.http, environment.serverUrl.academic, {
          studentId,
          body: {
            tipoSangre: record.tipoSangre,
            contactoPrincipalNombre: record.contactoPrincipalNombre,
            contactoPrincipalParentesco: record.contactoPrincipalParentesco,
            contactoPrincipalTelCelular: record.contactoPrincipalTelCelular,
            contactoPrincipalTelTrabajo: record.contactoPrincipalTelTrabajo,
            contactoSecundarioNombre: record.contactoSecundarioNombre,
            contactoSecundarioParentesco: record.contactoSecundarioParentesco,
            contactoSecundarioTelCelular: record.contactoSecundarioTelCelular,
            contactoSecundarioTelTrabajo: record.contactoSecundarioTelTrabajo,
            alergiaMedicamentos: record.alergiaMedicamentos,
            alergiaAlimentos: record.alergiaAlimentos,
            alergiaAmbiental: record.alergiaAmbiental,
            sinAlergiasConocidas: record.sinAlergiasConocidas,
            usaLentes: record.usaLentes,
            usaAparatoAuditivo: record.usaAparatoAuditivo,
            usaOrtopedico: record.usaOrtopedico,
            otroApoyo: record.otroApoyo,
            asma: record.asma,
            convulsiones: record.convulsiones,
            diabetesInfantil: record.diabetesInfantil,
            problemasCardiacos: record.problemasCardiacos,
            otroPadecimiento: record.otroPadecimiento,
            tomaMedicamentoRegular: record.tomaMedicamentoRegular,
            nombreMedicamento: record.nombreMedicamento,
            dosisHorario: record.dosisHorario,
            requiereAdministracionEscolar: record.requiereAdministracionEscolar,
            tieneRestriccionEF: record.tieneRestriccionEF,
            motivoRestriccionEF: record.motivoRestriccionEF,
            restriccionesAlimentarias: record.restriccionesAlimentarias,
            notasAdicionales: record.notasAdicionales,
          },
        }).pipe(
          map((response) =>
            HistorialMedicoActions.saveRecordSuccess({ record: toHistorialMedico(response.body?.data ?? {}) })
          ),
          catchError(() =>
            of(HistorialMedicoActions.saveRecordFailure({ error: 'No se pudo guardar el historial médico.' }))
          )
        )
      )
    )
  );
}
